/**
 * Centralized QR Code Print Service
 *
 * This service provides a unified QR code printing functionality across the entire application.
 * It uses the EXACT working code from QRCodeGenerator.jsx batch print that was working yesterday.
 *
 * Usage:
 *   import { printQRCodes, printSingleQRCode } from '../services/qrCodePrintService';
 *   printQRCodes([item1, item2, ...]);
 *   printSingleQRCode(item);
 */

/**
 * Default QR code print preferences
 */
const DEFAULT_PREFERENCES = {
  qrSize: 256,
  includeName: true,
  includeSerial: true,
  includeJobName: true,
  includeSiteName: true,
  includeModel: true,
  showCustomLabel: false,
  customLabel: ""
};

/**
 * Load user preferences from localStorage
 */
const loadPreferences = () => {
  try {
    const saved = localStorage.getItem('qrCodePreferences');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn('Could not load QR preferences from localStorage:', error);
  }
  return DEFAULT_PREFERENCES;
};

/**
 * Save user preferences to localStorage
 */
export const savePrintPreferences = (prefs) => {
  try {
    localStorage.setItem('qrCodePreferences', JSON.stringify(prefs));
  } catch (error) {
    console.warn('Could not save QR preferences to localStorage:', error);
  }
};

/**
 * Print QR codes for one or more items
 * EXACT COPY FROM QRCodeGenerator.jsx batch print function (lines 240-513)
 *
 * @param {Array} items - Array of items to print QR codes for
 * @returns {Promise<boolean>} - Returns true if print window was opened successfully
 */
export const printQRCodes = async (items) => {
  // Validate input
  if (!items || items.length === 0) {
    console.warn('qrCodePrintService: No items provided for printing');
    return false;
  }

  // Ensure items is an array
  const itemsArray = Array.isArray(items) ? items : [items];

  // Load preferences
  const preferences = loadPreferences();
  const {
    includeJobName,
    includeSiteName,
    includeModel,
    includeName,
    includeSerial,
    showCustomLabel,
    customLabel
  } = preferences;

  try {
    // Open print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Popup blocked! Please allow popups for this site to print QR codes.');
      return false;
    }

    // Generate HTML for all QR codes, each on its own page
    // OPTIMIZED: QR codes now contain JUST the equipment_id for instant scanning
    // Scanner types ~10 characters (<0.5s) instead of 100+ characters (5+ seconds)
    // ScanInput.jsx already handles extracting IDs from URLs or using raw IDs
    const allLabelsHtml = itemsArray.map((batchItem, index) => {
      // Use JUST the equipment ID for fast scanner input
      // When scanned in Quick Actions, this gets typed into ScanInput instantly
      const qrUrl = batchItem.equipment_id || batchItem.id;

      // Build the info section HTML
      let jobInfo = '';
      if (includeJobName && (batchItem.job_name || batchItem.job_number || batchItem.job_id || batchItem.current_job_id)) {
        const jobParts = [];
        const jobId = batchItem.job_number || batchItem.job_id || batchItem.current_job_id;
        if (jobId) jobParts.push(jobId);
        if (batchItem.job_name) jobParts.push(batchItem.job_name);
        jobInfo = `<div class="item-job">${jobParts.join(' - ')}</div>`;
      }

      return {
        url: qrUrl,
        html: `
          <div class="page">
            <div class="label">
              <div class="qr-container">
                <div class="qr-section">
                  <canvas id="qr-code-${index}" class="qr-image"></canvas>
                </div>
                <div class="info-section">
                  ${jobInfo}
                  ${(includeSiteName && (batchItem.site_name || batchItem.site_id || batchItem.current_site_id)) ?
                    `<div class="item-site">${batchItem.site_name || batchItem.site_id || batchItem.current_site_id}</div>` : ''}
                  ${(includeModel && batchItem.model) ?
                    `<div class="item-model">${batchItem.model}</div>` : ''}
                  ${(includeName && (batchItem.description || batchItem.name)) ?
                    `<div class="item-name">${batchItem.description || batchItem.name}</div>` : ''}
                  ${(includeSerial && batchItem.serial_number) ?
                    `<div class="item-serial">${batchItem.serial_number}</div>` : ''}
                  ${showCustomLabel && customLabel ?
                    `<div class="item-custom">${customLabel}</div>` : ''}
                  ${(batchItem.equipment_id || batchItem.id) ?
                    `<div class="item-id">Item ID: ${batchItem.equipment_id || batchItem.id}</div>` : ''}
                </div>
              </div>
            </div>
          </div>
        `
      };
    });

    const qrUrls = allLabelsHtml.map(item => item.url);
    const htmlContent = allLabelsHtml.map(item => item.html).join('');

    // EXACT CSS AND STRUCTURE FROM QRCodeGenerator.jsx lines 306-513
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Codes - Batch Print (${itemsArray.length} items)</title>
          <style>
            @page {
              size: 4in 2in landscape;
              margin: 0;
            }
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
            }
            .page {
              width: 4in;
              height: 2in;
              page-break-after: always;
              page-break-inside: avoid;
              position: relative;
            }
            .page:last-child {
              page-break-after: auto;
            }
            .label {
              width: 4in;
              height: 2in;
              max-width: 4in;
              max-height: 2in;
              display: flex;
              align-items: center;
              padding: 0.05in;
              box-sizing: border-box;
              overflow: hidden;
            }
            .qr-container {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: flex-start;
              width: 100%;
              height: 100%;
              max-width: 100%;
              max-height: 100%;
              gap: 0.08in;
              padding: 0;
              box-sizing: border-box;
              overflow: hidden;
            }
            .qr-section {
              flex-shrink: 0;
              width: 1.4in;
              height: 1.4in;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 0.04in;
              padding: 0.1in;
              background: white;
            }
            .qr-image {
              width: 1.1in !important;
              height: 1.1in !important;
              max-width: 1.1in;
              max-height: 1.1in;
              object-fit: contain;
              display: block;
              padding: 0.05in;
              background: white;
            }
            .info-section {
              flex: 1;
              display: flex;
              flex-direction: column;
              justify-content: center;
              height: 1.7in;
              overflow: visible;
              padding: 0 0.02in 0 0;
              max-width: calc(4in - 1in - 0.08in - 0.05in - 0.05in);
            }
            .item-model {
              font-size: 10pt;
              font-weight: bold;
              line-height: 1.1;
              margin-bottom: 0.02in;
              word-wrap: break-word;
              word-break: break-word;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              max-width: 100%;
              color: #000;
            }
            .item-name {
              font-size: 10pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 0.02in;
              word-wrap: break-word;
              overflow: hidden;
              display: -webkit-box;
              -webkit-line-clamp: 1;
              -webkit-box-orient: vertical;
            }
            .item-serial {
              font-size: 10pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 0.02in;
              word-break: break-all;
            }
            .item-job {
              font-size: 10pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 0.02in;
              word-wrap: break-word;
              word-break: break-word;
              overflow-wrap: break-word;
            }
            .item-site {
              font-size: 10pt;
              font-weight: bold;
              color: #000;
              margin-bottom: 0.02in;
              word-wrap: break-word;
              word-break: break-word;
              overflow-wrap: break-word;
            }
            .item-id {
              font-size: 10pt;
              font-weight: bold;
              color: #000;
              word-wrap: break-word;
              word-break: break-word;
              overflow-wrap: break-word;
            }
            @media print {
              html, body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .page {
                margin: 0;
                box-shadow: none;
                page-break-after: always;
              }
              .page:last-child {
                page-break-after: auto;
              }
            }
            @media screen {
              .page {
                margin: 10px auto;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.1/build/qrcode.min.js"></script>
        </head>
        <body>
          ${htmlContent}
          <script>
            window.onload = function() {
              const qrUrls = ${JSON.stringify(qrUrls)};
              let completed = 0;

              qrUrls.forEach((url, index) => {
                const canvas = document.getElementById('qr-code-' + index);
                if (!canvas) {
                  console.error('Canvas not found for index ' + index);
                  completed++;
                  return;
                }

                QRCode.toCanvas(canvas, url, {
                  width: 192,
                  margin: 0
                }, function(error) {
                  if (error) console.error('QR Code generation error:', error);
                  completed++;

                  // When all QR codes are generated, trigger print
                  if (completed === qrUrls.length) {
                    setTimeout(function() {
                      window.print();
                      setTimeout(function() {
                        window.close();
                      }, 500);
                    }, 100);
                  }
                });
              });
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
    return true;
  } catch (error) {
    console.error('Failed to generate QR codes:', error);
    alert('Failed to generate QR codes. Please try again.');
    return false;
  }
};

/**
 * Print a single QR code (convenience wrapper)
 */
export const printSingleQRCode = (item) => {
  return printQRCodes([item]);
};

/**
 * Export for compatibility
 */
export const loadPrintPreferences = loadPreferences;

export default {
  printQRCodes,
  printSingleQRCode,
  loadPrintPreferences,
  savePrintPreferences,
  DEFAULT_PREFERENCES
};
